import { Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { AuthService } from 'src/app/services/auth.service';
import { ErrorService } from 'src/app/services/error.service';
import { InvoicesService } from 'src/app/services/invoices.service';
import {
  CategorizedLicenses,
  DesktopLicenseDetails,
  DesktopLicenseWithProduct,
  LicensesService,
  TransferLicensesResponse,
} from 'src/app/services/licenses.service';
import {
  DesktopLicenseRecords,
  MachinesService,
  RegistrationRecord,
} from 'src/app/services/machines.service';
import { ToastService } from 'src/app/services/toast.service';

@Component({
  selector: 'app-licenses-page',
  templateUrl: './licenses-page.component.html',
  styleUrls: ['./licenses-page.component.scss'],
})
export class LicensesPageComponent implements OnDestroy {
  @ViewChild('assignInitialUniqueIDDialog')
  assignInitialUniqueIDDialog!: ElementRef<HTMLDialogElement>;

  @ViewChild('transferDialog') transferDialog!: ElementRef<HTMLDialogElement>;
  @ViewChild('newUniqueIdInput')
  newUniqueIdInput!: ElementRef<HTMLInputElement>;

  loadingLicenses = false;
  licenses?: CategorizedLicenses;
  errorFetchingLicenses: string = '';

  licenseWithRenewWarningId?: string;
  creatingInvoice: boolean = false;
  addingLicensesToCart: boolean = false;
  licenseBeingAddedToCart?: DesktopLicenseWithProduct = undefined;

  shownKeys = new Set<string>();
  hiddenKeyString = [9, 10, 10, 3, 7]
    .map((length) => '&bull;'.repeat(length))
    .join('-');

  initialUniqueIDs: { [key: string]: string } = {};
  applyingInitialUniqueIds = false;
  distinctActiveMachines: RegistrationRecord[] = [];
  loadingMachines = false;
  errorFetchingMachines: string = '';
  copyFeedbackRegistrationId: number | null = null;
  private copyTooltipClearTimer?: ReturnType<typeof setTimeout>;

  transferingLicense = false;
  errorTransferingLicense?: string;
  licenseToTransfer: DesktopLicenseDetails | null = null;
  newUniqueId = '';
  sameUidLicensesList: DesktopLicenseDetails[] = [];
  sameUidSelected = new Set<string>();
  transferDistinctActiveMachinesList: RegistrationRecord[] = [];
  transferLoadingMachines = false;
  transferErrorLoadingMachines?: string;
  selectedTransferMachineId?: string;

  constructor(
    private authService: AuthService,
    private errorService: ErrorService,
    private invoicesService: InvoicesService,
    private licensesService: LicensesService,
    private machinesService: MachinesService,
    private toastService: ToastService
  ) {
    this.fetchLicenses();
  }

  ngOnDestroy(): void {
    this.clearCopyTooltip();
  }

  get userCanCreateInvoice() {
    // only internal admins, owner, or admin users can create invoices
    return this.authService.hasAnyPermissions([
      'super_admin',
      'internal_admin',
      'admin',
      'owner',
    ]);
  }

  get showApplyInitialUniqueIdsButton(): boolean {
    const active = this.licenses?.active ?? [];
    return (
      active.length > 0 && active.every((l) => !l.activation_key?.unique_id)
    );
  }

  get activeLicensesWithoutUniqueIDs(): DesktopLicenseDetails[] {
    return (
      this.licenses?.active.filter((l) => !l.activation_key?.unique_id) ?? []
    );
  }

  get closeToExpiringLicenses() {
    return (
      this.licenses?.active
        .filter((license) => license.close_to_expiration)
        .concat(
          this.licenses?.pending.filter(
            (license) => license.close_to_expiration
          ) ?? []
        ) ?? []
    );
  }

  fetchLicenses() {
    this.loadingLicenses = true;
    this.licensesService.getLicensesForCurrentUser(true).subscribe({
      next: (res) => {
        this.loadingLicenses = false;
        this.sortLicenses(res.desktop_licenses);
        console.log('licenses: ', this.licenses);
      },
      error: (err) => {
        this.loadingLicenses = false;
        console.error(err);
        this.errorFetchingLicenses = err;
      },
    });
  }

  sortLicenses(licenses: CategorizedLicenses) {
    this.licenses = licenses;
    for (const t of ['active', 'pending', 'expired', 'disabled']) {
      const key = t as keyof CategorizedLicenses;
      if (this.licenses[key]) {
        const sorted = this.licensesService.sortLicensesByRelationship(
          licenses[key]
        );
        this.licenses[key] = sorted as DesktopLicenseDetails[];
      }
    }
  }

  hasActiveInvoice(licenseDetails: DesktopLicenseDetails) {
    if (!licenseDetails.invoices || licenseDetails.invoices.length === 0) {
      return false;
    }
    const now = new Date();
    for (const invoice of licenseDetails.invoices) {
      const expirationDate = new Date(licenseDetails.expiration ?? '');
      if (!invoice.completed && expirationDate > now) {
        return true;
      }
    }
    return false;
  }

  getLicenseType(license: DesktopLicenseWithProduct) {
    return this.licensesService.getType(license);
  }

  toggleRenew(license: DesktopLicenseWithProduct, ev: Event) {
    console.log('toggle renew - license: ', license);
    if (license.will_renew) {
      ev.preventDefault();
      this.licenseWithRenewWarningId = license.id;
    } else if (!license.will_renew) {
      this.licenseWithRenewWarningId = undefined;
      license.will_renew = true;
      this.saveLicense(license);
    }
  }

  disableLicenseRenewal(license: DesktopLicenseWithProduct) {
    license.will_renew = false;
    this.saveLicense(license);
  }

  saveLicense(license: DesktopLicenseWithProduct) {
    console.log('saving - license: ', license);
    this.licenseWithRenewWarningId = undefined;
    this.licensesService
      .updateLicenseWillRenew([
        {
          desktop_license_id: license.id,
          will_renew: license.will_renew,
        },
      ])
      .subscribe({
        next: (data) => {
          this.toastService.addToast(
            'success',
            'License settings updated successfully'
          );
          console.log('data: ', data);
        },
        error: (err) => {
          this.toastService.addToast(
            'error',
            'Error updating license settings'
          );
          console.log('err: ', err);
        },
      });
  }

  async addLicenseToCart(license: DesktopLicenseWithProduct) {
    if (this.addingLicensesToCart) {
      return;
    }

    this.licenseBeingAddedToCart = license;
    this.addingLicensesToCart = true;

    this.invoicesService
      .addLicenseToOrCreatePendingInvoice(license.id)
      .subscribe({
        next: (invoice) => {
          this.addingLicensesToCart = false;
          this.licenseBeingAddedToCart = undefined;
          this.invoicesService.pendingInvoice = invoice.invoice_details;
        },
        error: (err) => {
          this.addingLicensesToCart = false;
          this.licenseBeingAddedToCart = undefined;
          console.error(err);
        },
      });
  }

  isInCart(license: DesktopLicenseWithProduct) {
    return this.invoicesService.isInPendingInvoice(license.id);
  }

  get pendingInvoiceHasManualValues() {
    return (
      this.invoicesService.pendingInvoice?.desktop_invoice.use_manual_values ??
      false
    );
  }

  private parseLastUsedMs(lastused?: string): number {
    if (!lastused) {
      return 0;
    }
    const t = Date.parse(lastused);
    return Number.isNaN(t) ? 0 : t;
  }

  private getDistinctActiveMachines(
    records: DesktopLicenseRecords
  ): RegistrationRecord[] {
    const list = records.active ?? [];
    const byUnique = new Map<string, RegistrationRecord>();
    for (const m of list) {
      const uid = m.uniqueid?.trim();
      if (!uid) {
        continue;
      }
      const existing = byUnique.get(uid);
      if (!existing) {
        byUnique.set(uid, m);
        continue;
      }
      if (
        this.parseLastUsedMs(m.lastused) >=
        this.parseLastUsedMs(existing.lastused)
      ) {
        byUnique.set(uid, m);
      }
    }
    return Array.from(byUnique.values()).sort(
      (a, b) =>
        this.parseLastUsedMs(b.lastused) - this.parseLastUsedMs(a.lastused)
    );
  }

  private clearCopyTooltip(): void {
    if (this.copyTooltipClearTimer !== undefined) {
      clearTimeout(this.copyTooltipClearTimer);
      this.copyTooltipClearTimer = undefined;
    }
    this.copyFeedbackRegistrationId = null;
  }

  copyMachineUniqueId(uniqueId: string | undefined, registrationId: number) {
    const id = uniqueId?.trim();
    if (!id) {
      return;
    }
    navigator.clipboard.writeText(id).then(() => {
      if (this.copyTooltipClearTimer !== undefined) {
        clearTimeout(this.copyTooltipClearTimer);
      }
      this.copyFeedbackRegistrationId = registrationId;
      this.copyTooltipClearTimer = setTimeout(() => {
        this.copyFeedbackRegistrationId = null;
        this.copyTooltipClearTimer = undefined;
      }, 2000);
    });
  }

  pasteUniqueIdIntoLicense(license: DesktopLicenseDetails) {
    navigator.clipboard.readText().then(
      (text) => {
        const t = text.trim();
        this.initialUniqueIDs = {
          ...this.initialUniqueIDs,
          [license.id]: t,
        };
      },
      () => {
        this.toastService.addToast('error', 'Could not read clipboard');
      }
    );
  }

  private getMachinesForCurrentTenant() {
    const tenantId = this.authService.currentUser?.tenant_id;
    if (!tenantId) {
      return;
    }
    this.loadingMachines = true;
    this.errorFetchingMachines = '';
    this.distinctActiveMachines = [];
    this.machinesService.getMachinesForTenant(tenantId).subscribe({
      next: (machines) => {
        this.loadingMachines = false;
        this.distinctActiveMachines = this.getDistinctActiveMachines(machines);
      },
      error: (err) => {
        console.error(err);
        this.loadingMachines = false;
        this.errorFetchingMachines =
          this.errorService.getErrorMessage(err) ||
          'Could not load active machines.';
        this.distinctActiveMachines = [];
      },
    });
  }

  showInitialUniqueIDDialog() {
    this.initialUniqueIDs = {};
    this.clearCopyTooltip();
    this.getMachinesForCurrentTenant();
    this.assignInitialUniqueIDDialog.nativeElement.showModal();
  }

  closeAssignInitialUniqueIDDialog() {
    this.initialUniqueIDs = {};
    this.applyingInitialUniqueIds = false;
    this.distinctActiveMachines = [];
    this.clearCopyTooltip();
    this.assignInitialUniqueIDDialog.nativeElement.close();
  }

  handleInitialUniqueIDChange(value: string, license: DesktopLicenseDetails) {
    this.initialUniqueIDs = {
      ...this.initialUniqueIDs,
      [license.id]: value,
    };
  }

  applyInitialUniqueIDs() {
    const tenantId = this.authService.currentUser?.tenant_id;
    if (!tenantId) {
      return;
    }
    const updates = Object.entries(this.initialUniqueIDs)
      .filter(([, uid]) => uid?.trim())
      .map(([license_id, unique_id]) => ({
        license_id,
        unique_id: unique_id.trim(),
      }));
    if (updates.length === 0) {
      this.toastService.addToast(
        'error',
        'Enter at least one Unique ID for a license row.'
      );
      return;
    }
    this.applyingInitialUniqueIds = true;
    this.licensesService
      .applyInitialUniqueIds({
        tenant_id: tenantId,
        generate_activation_keys: true,
        updates,
      })
      .subscribe({
        next: () => {
          this.applyingInitialUniqueIds = false;
          this.toastService.addToast('success', 'Unique IDs applied.');
          this.closeAssignInitialUniqueIDDialog();
          this.fetchLicenses();
        },
        error: (err: unknown) => {
          console.error(err);
          this.applyingInitialUniqueIds = false;
          const errorMessage = this.errorService.getErrorMessage(err);
          this.toastService.addToast(
            'error',
            errorMessage || 'Could not apply Unique IDs.'
          );
        },
      });
  }

  /** One row per Unique ID from active desktop registration records; duplicate UIDs keep latest lastused. */
  private distinctActiveByUniqueId(
    active: RegistrationRecord[]
  ): RegistrationRecord[] {
    const lastUsedMs = (r: RegistrationRecord): number => {
      if (!r.lastused) {
        return 0;
      }
      const t = new Date(r.lastused).getTime();
      return Number.isNaN(t) ? 0 : t;
    };
    const pickBetter = (
      a: RegistrationRecord,
      b: RegistrationRecord
    ): RegistrationRecord => {
      const ta = lastUsedMs(a);
      const tb = lastUsedMs(b);
      if (tb !== ta) {
        return tb > ta ? b : a;
      }
      return a.registration_id >= b.registration_id ? a : b;
    };

    const byUid = new Map<string, RegistrationRecord>();
    for (const rec of active) {
      const uid = rec.uniqueid?.trim();
      if (!uid) {
        continue;
      }
      const prev = byUid.get(uid);
      byUid.set(uid, prev ? pickBetter(prev, rec) : rec);
    }

    return [...byUid.values()].sort((a, b) => lastUsedMs(b) - lastUsedMs(a));
  }

  licenseCurrentUniqueId(license: DesktopLicenseDetails): string | null {
    const uid =
      license.activation_key?.unique_id ??
      (license.unique_id != null ? String(license.unique_id) : undefined);
    if (!uid || uid.trim() === '') {
      return null;
    }
    return uid.trim();
  }

  private getSameUidLicenses(
    license: DesktopLicenseDetails
  ): DesktopLicenseDetails[] {
    const uid = this.licenseCurrentUniqueId(license);
    const pool = this.licenses?.active ?? [];
    if (!uid) {
      return [];
    }
    return pool.filter(
      (l) => l.active && this.licenseCurrentUniqueId(l) === uid
    );
  }

  setSameUidSelected(licenseId: string, checked: boolean): void {
    if (checked) {
      this.sameUidSelected.add(licenseId);
    } else {
      this.sameUidSelected.delete(licenseId);
    }
  }

  selectRegistrationForTransfer(machine: RegistrationRecord): void {
    this.selectedTransferMachineId = String(machine.registration_id);
    if (this.newUniqueIdInput) {
      this.newUniqueIdInput.nativeElement.value = '';
    }
  }

  clearSelectedTransferMachineId(): void {
    this.selectedTransferMachineId = undefined;
  }

  isRegistrationSelected(machine: RegistrationRecord): boolean {
    return this.selectedTransferMachineId === String(machine.registration_id);
  }

  licenseIdsToTransfer(): string[] {
    if (this.sameUidLicensesList.length === 0 && this.licenseToTransfer) {
      return [this.licenseToTransfer.id];
    }
    return this.sameUidLicensesList
      .filter((l) => this.sameUidSelected.has(l.id))
      .map((l) => l.id);
  }

  showTransferDialog(license: DesktopLicenseDetails): void {
    this.resetTransferDialog();

    this.licenseToTransfer = license;
    this.sameUidLicensesList = this.getSameUidLicenses(license);
    this.sameUidSelected.clear();
    for (const p of this.sameUidLicensesList) {
      this.sameUidSelected.add(p.id);
    }

    const tenantId =
      license.tenant_id || this.authService.currentUser?.tenant_id;
    if (tenantId) {
      this.loadMachinesForTransfer(tenantId);
    } else {
      this.transferLoadingMachines = false;
      this.transferErrorLoadingMachines = 'No tenant for license.';
    }

    this.transferDialog.nativeElement.showModal();
    setTimeout(() => {
      this.newUniqueIdInput?.nativeElement?.focus();
    }, 100);
  }

  closeTransferDialog(): void {
    this.transferDialog.nativeElement.close();
    this.resetTransferDialog();
    this.licenseToTransfer = null;
  }

  resetTransferDialog(): void {
    this.newUniqueId = '';
    this.selectedTransferMachineId = undefined;
    this.transferDistinctActiveMachinesList = [];
    this.transferLoadingMachines = false;
    this.transferErrorLoadingMachines = undefined;
    this.sameUidLicensesList = [];
    this.sameUidSelected.clear();
    this.errorTransferingLicense = undefined;
  }

  private loadMachinesForTransfer(tenantId: string): void {
    this.transferLoadingMachines = true;
    this.transferErrorLoadingMachines = undefined;

    this.machinesService.getMachinesForTenant(tenantId).subscribe({
      next: (response) => {
        this.transferDistinctActiveMachinesList = this.distinctActiveByUniqueId(
          response.active ?? []
        );
        // filter out the machine that is the same as the current unique id for
        // the license they're transferring
        this.transferDistinctActiveMachinesList =
          this.transferDistinctActiveMachinesList.filter(
            (m) =>
              m.uniqueid?.trim() !==
              this.licenseToTransfer?.activation_key?.unique_id?.trim()
          );
        this.transferLoadingMachines = false;
        this.transferErrorLoadingMachines = undefined;
      },
      error: (error: unknown) => {
        this.transferDistinctActiveMachinesList = [];
        this.transferLoadingMachines = false;
        this.transferErrorLoadingMachines =
          this.errorService.getErrorMessage(error) ||
          'Could not load machines.';
      },
    });
  }

  transferLicense(): void {
    if (!this.licenseToTransfer) {
      return;
    }

    this.transferingLicense = true;
    this.errorTransferingLicense = undefined;

    let newUniqueId: string | undefined;
    if (this.newUniqueId && this.newUniqueId.trim() !== '') {
      newUniqueId = this.newUniqueId.trim();
    } else if (this.selectedTransferMachineId) {
      const machine = this.transferDistinctActiveMachinesList.find(
        (m) =>
          String(m.registration_id) === String(this.selectedTransferMachineId)
      );
      newUniqueId = machine?.uniqueid?.trim();
      if (!newUniqueId) {
        this.transferingLicense = false;
        this.errorTransferingLicense =
          'Could not resolve Unique ID for selected machine.';
        return;
      }
    } else {
      this.transferingLicense = false;
      this.errorTransferingLicense =
        'Enter a new Unique ID or select an existing machine.';
      return;
    }

    const ids = this.licenseIdsToTransfer();
    if (ids.length === 0) {
      this.transferingLicense = false;
      this.errorTransferingLicense = 'Select at least one license to transfer.';
      return;
    }

    this.licensesService
      .transferLicenses(ids, newUniqueId, true, true)
      .subscribe({
        next: (response) => this.handleLicensesTransferred(response),
        error: (error: unknown) => this.handleLicenseTransferError(error),
      });
  }

  private handleLicensesTransferred(response: TransferLicensesResponse): void {
    this.transferingLicense = false;
    this.errorTransferingLicense = undefined;

    const n = response.activation_keys.length;
    this.toastService.addToast(
      'success',
      n === 1
        ? 'License transferred successfully'
        : `${n} licenses transferred successfully`
    );
    this.closeTransferDialog();
    this.fetchLicenses();
  }

  private handleLicenseTransferError(error: unknown): void {
    this.transferingLicense = false;
    const errMessage = this.errorService.getErrorMessage(error);
    this.errorTransferingLicense = errMessage;
    this.toastService.addToast(
      'error',
      'Error transferring license: ' + errMessage
    );
  }
}
